import { CreditsNavChip } from "@/components/features/Credits/CreditsNavChip";
import { XIcon } from "@/components/socialIcons";
import { ScrollArea, ScrollBar } from "@/components/ui/scroll-area";
import { DISCORD_URL, INSTAGRAM_URL, X_URL } from "@/config/social";
import Card from "@/design-system/Card/Card";
import { IconButton } from "@/design-system/IconButton/IconButton";
import { ItemScoreDiamond } from "@/design-system/ItemScore/ItemScoreDiamond";
import { NewTag } from "@/design-system/Tags/NewTag/NewTag";
import { trackNavBarUpgrade } from "@/events/navBarMixpanel";
import { storeSignupContext } from "@/events/signupTracking";
import { OnboardingProgressBadge } from "@/features/MyClub/TryNext/onboardingProgress";
import { WingsCupMenuLink } from "@/features/WingsCup/WingsCup";
import { useBrowserCompatibility } from "@/hooks/useBrowserCompatability";
import { useOutsideClick } from "@/hooks/useOutsideClick";
import { cn } from "@/lib/utils";
import { AndroidIcon, AppleIcon } from "@/pages/AppLanding/PlatformIcons";
import { detectPlatform } from "@/pages/AppLanding/detectPlatform";
import { showModal } from "@/redux/message/messageSlice";
import { AppDispatch } from "@/redux/store";
import { useTranslation } from "@repo/shared/i18n";
import { CircleUserRound, House, X } from "lucide-react";
import { ReactNode, useEffect, useLayoutEffect, useRef, useState } from "react";
import { useDispatch } from "react-redux";
import { Link } from "react-router";

import { ContentLanguageDrawerItem } from "../../ContentLanguagePicker";
import {
  SiteNavPage,
  SiteNavPageKey,
  SiteNavSectionKey,
  findActiveNav,
  useSiteNavSections,
} from "../../siteNav/siteNavConfig";
import { navigationSquadBuilderIcon } from "./NavIcons";

const Divider = () => <div className="mx-4 my-2 h-px bg-edge" />;

/** Mobile keeps the most used destinations up top, one tap each. */
const TILES: { section: SiteNavSectionKey; href?: string }[] = [
  { section: "sbcs" },
  { section: "players" },
  { section: "squads" },
  // The hub's tabs cover the evo sub pages, so mobile skips them.
  { section: "evolutions", href: "/evolutions" },
  { section: "gallery" },
  { section: "club" },
];
const TILE_ICON = "size-4 shrink-0 text-icon";
// Mobile-only icons that aren't lucide: the legacy squad builder glyph, a greyed Item Score diamond.
const TILE_ICONS: Partial<Record<SiteNavSectionKey, ReactNode>> = {
  squads: (
    <span className={`block ${TILE_ICON}`}>{navigationSquadBuilderIcon}</span>
  ),
};
const PLATFORM_ICONS: Record<string, ReactNode> = {
  ios: <AppleIcon className={TILE_ICON} />,
  android: <AndroidIcon className={TILE_ICON} />,
};
const TOOL_ICONS: Partial<Record<SiteNavPageKey, ReactNode>> = {
  cheapestItemScore: (
    <span className="flex size-5 items-center justify-center grayscale">
      <ItemScoreDiamond size={18} />
    </span>
  ),
};
const TOOL_PAGES: SiteNavPageKey[] = [
  "galleryLevels",
  "objectives",
  "tactics",
  "playstylesLab",
  "compare",
  "cheapestItemScore",
];
const INFO_PAGES: SiteNavPageKey[] = [
  "metaRating",
  "evoTracker",
  "essentials",
  "news",
  "contact",
];

/** The mobile menu: a tile grid of the main destinations, then the rest as links. */
export const Drawer = ({
  isDrawerOpen,
  currentRoute,
  onClose,
  userIsLoggedIn,
}: {
  isDrawerOpen: boolean;
  currentRoute: string;
  onClose: () => void;
  userIsLoggedIn: boolean;
}) => {
  const { t } = useTranslation(["nav", "common"]);
  const dispatch = useDispatch<AppDispatch>();
  const { isUnsupportedBrowser } = useBrowserCompatibility();
  const drawerRef = useOutsideClick(onClose);
  const sections = useSiteNavSections();
  const active = findActiveNav(sections, currentRoute);
  const pages = sections.flatMap((section) => section.pages);
  const findPage = (key: SiteNavPageKey) => pages.find((p) => p.key === key)!;
  const upgrade = findPage("subscription");
  const app = findPage("appLanding");

  /*
    Links leave the drawer open: it closes when the route commits, in the same
    frame as the new page. Closing on tap lets the drawer go first while the
    router renders the page as a transition, which flashes the old page.
  */
  const lastRoute = useRef(currentRoute);
  useLayoutEffect(() => {
    if (lastRoute.current === currentRoute) return;
    lastRoute.current = currentRoute;
    onClose();
  }, [currentRoute, onClose]);
  // The tapped item lights up at once, while the page renders behind.
  const [pending, setPending] = useState<string>();
  useEffect(() => {
    if (!isDrawerOpen) setPending(undefined);
  }, [isDrawerOpen]);
  const selected = (href: string, isCurrent: boolean) =>
    pending ? pending === href : isCurrent;
  const closeIfHere = (href: string) => {
    setPending(href);
    // The route will not change, so nothing else would close it.
    if (href === currentRoute) onClose();
  };

  const content = (
    <nav
      className={cn(
        "fixed top-0 right-0 bottom-0 z-100 flex w-full justify-end bg-black/60",
        isDrawerOpen ? "translate-x-0" : "translate-x-full",
      )}
    >
      <div
        ref={drawerRef}
        className={cn(
          "flex h-full w-full max-w-[400px] flex-col border-l border-edge bg-background transition-transform duration-200",
          isDrawerOpen ? "translate-x-0" : "translate-x-full",
        )}
      >
        <div className="flex h-12 shrink-0 items-center justify-between pr-3 pl-4">
          <Link
            to="/"
            onClick={() => closeIfHere("/")}
            aria-label={t("goHome")}
            className="flex items-center gap-x-2.5 text-sm font-medium text-foreground"
          >
            <img src="/logo.png" className="size-6" alt="" />
            {t("common:brand")}
          </Link>
          <IconButton
            aria-label={t("siteNav.close")}
            icon={<X className="size-4 text-foreground" />}
            onClick={(e) => {
              e.stopPropagation();
              onClose();
            }}
          />
        </div>

        <ScrollArea className="flex-1">
          <div className="ad-gutter flex flex-col pb-20">
            <div className="flex items-center justify-between px-4 pt-2 pb-3">
              {userIsLoggedIn ? (
                <Link
                  to="/profile"
                  onClick={() => closeIfHere("/profile")}
                  className="flex items-center gap-x-2.5 text-sm font-medium text-foreground-muted"
                >
                  <CircleUserRound className="size-5" strokeWidth={1.5} />
                  {t("common:profile")}
                </Link>
              ) : (
                <button
                  type="button"
                  className="flex items-center gap-x-2.5 text-sm font-medium text-foreground-muted"
                  onClick={(e) => {
                    e.stopPropagation();
                    onClose();
                    storeSignupContext("cta", { section: "Nav Bar" });
                    dispatch(showModal({ modalType: "login" }));
                  }}
                >
                  <CircleUserRound className="size-5" strokeWidth={1.5} />
                  {t("common:logIn")}
                </button>
              )}
              {/* The chip navigates to the credits explainer on /subscription. */}
              <span
                onClick={() => currentRoute === "/subscription" && onClose()}
              >
                <CreditsNavChip compact />
              </span>
            </div>
            <div className="grid grid-cols-2 gap-2 px-4 pt-1 pb-3">
              <DrawerTile
                href="/"
                icon={<House className={TILE_ICON} />}
                label={t("links.home")}
                isCurrent={selected("/", currentRoute === "/")}
                onNavigate={closeIfHere}
              />
              {TILES.map(({ section: key, href }) => {
                const section = sections.find((s) => s.key === key)!;
                const page = section.pages[0];
                return (
                  <DrawerTile
                    key={key}
                    href={href ?? section.href}
                    icon={
                      TILE_ICONS[key] ?? <section.icon className={TILE_ICON} />
                    }
                    label={
                      // Gallery's pages aren't grouped here, so they need the section in the name.
                      key === "gallery"
                        ? t("siteNav.pages.galleryPlanner.mobileLabel")
                        : section.label
                    }
                    tag={page.tag && t(`common:tags.${page.tag}`)}
                    badge={key === "club" && <OnboardingProgressBadge />}
                    isCurrent={selected(
                      href ?? section.href,
                      active.sectionKey === key,
                    )}
                    onNavigate={closeIfHere}
                  />
                );
              })}
              <DrawerTile
                href={upgrade.href}
                icon={
                  <upgrade.icon
                    className={cn(
                      TILE_ICON,
                      upgrade.highlight && "text-primary-blue",
                    )}
                  />
                }
                label={upgrade.label}
                highlight={upgrade.highlight}
                isCurrent={selected(
                  upgrade.href,
                  active.pageKey === "subscription",
                )}
                onNavigate={(href) => {
                  trackNavBarUpgrade();
                  closeIfHere(href);
                }}
              />
              <DrawerTile
                href={app.href}
                icon={
                  // The visitor's own platform, so the tile reads as their app.
                  PLATFORM_ICONS[detectPlatform() ?? "none"] ?? (
                    <app.icon className={TILE_ICON} />
                  )
                }
                label={app.label}
                tag={app.tag && t(`common:tags.${app.tag}`)}
                isCurrent={selected(app.href, active.pageKey === "appLanding")}
                onNavigate={closeIfHere}
                className="col-span-2"
              />
            </div>
            <ContentLanguageDrawerItem userIsLoggedIn={userIsLoggedIn} />
            <Divider />
            <div className="flex flex-col">
              {TOOL_PAGES.map(findPage).map((page) => (
                <DrawerToolRow
                  key={page.key}
                  page={
                    page.key === "galleryLevels"
                      ? {
                          ...page,
                          label: t("siteNav.pages.galleryLevels.mobileLabel"),
                        }
                      : page
                  }
                  isCurrent={selected(page.href, page.key === active.pageKey)}
                  onNavigate={closeIfHere}
                />
              ))}
            </div>
            {/* Wings Cup campaign: the link only, no partner card on mobile. */}
            <WingsCupMenuLink variant="mobile" />
            <Divider />
            <DrawerLinks
              pages={INFO_PAGES.map(findPage).map((page) =>
                // The full title wraps in a half-width column.
                page.key === "evoTracker"
                  ? { ...page, label: t("siteNav.pages.evoTracker.shortLabel") }
                  : page,
              )}
              isCurrent={(page) =>
                selected(page.href, page.key === active.pageKey)
              }
              onNavigate={closeIfHere}
              subtle
            />

            <Divider />
            <div className="flex items-center gap-x-5 px-4 py-2 text-foreground-muted">
              <a href={DISCORD_URL} target="_blank" rel="noopener noreferrer">
                <img
                  src={
                    import.meta.env.VITE_APP_ASSETS_BASE_URL +
                    "/fut23/misc/discord-v2.svg"
                  }
                  className="size-6"
                  alt={t("common:discord")}
                />
              </a>
              <a
                href={X_URL}
                target="_blank"
                rel="noopener noreferrer"
                aria-label={t("links.x")}
              >
                <XIcon className="size-5 text-foreground" />
              </a>
              <a href={INSTAGRAM_URL} target="_blank" rel="noopener noreferrer">
                <img
                  src={
                    import.meta.env.VITE_APP_ASSETS_BASE_URL +
                    "/fut23/misc/instagram.svg"
                  }
                  className="size-7"
                  alt={t("links.instagram")}
                />
              </a>
            </div>
          </div>
          <ScrollBar orientation="vertical" />
        </ScrollArea>
      </div>
    </nav>
  );

  return isUnsupportedBrowser ? (isDrawerOpen ? content : null) : content;
};

const DrawerTile = ({
  href,
  icon,
  label,
  tag,
  badge,
  highlight,
  isCurrent,
  onNavigate,
  className,
}: {
  href: string;
  icon: ReactNode;
  label: string;
  tag?: string;
  /** A count beside the label, e.g. My Club's onboarding progress. */
  badge?: ReactNode;
  highlight?: boolean;
  isCurrent: boolean;
  onNavigate: (href: string) => void;
  className?: string;
}) => (
  <Link
    to={href}
    onClick={() => onNavigate(href)}
    aria-current={isCurrent ? "page" : undefined}
    className={className}
  >
    <Card
      hoverable={false}
      className={cn(
        "flex min-h-14 items-center gap-x-2.5 rounded-xl px-3 py-2 active:bg-neutral-750",
        isCurrent && "border-neutral-500",
      )}
    >
      {icon}
      <span className="flex flex-wrap items-center gap-1.5">
        <span
          className={`text-body ${highlight ? "text-primary-blue" : "text-foreground"}`}
        >
          {label}
        </span>
        {tag && <NewTag tag={tag} />}
        {badge}
      </span>
    </Card>
  </Link>
);

/** A secondary tool: icon and label, styled like the language row. */
const DrawerToolRow = ({
  page,
  isCurrent,
  onNavigate,
}: {
  page: SiteNavPage;
  isCurrent: boolean;
  onNavigate: (href: string) => void;
}) => {
  const { t } = useTranslation("common");
  return (
    <Link
      to={page.href}
      onClick={() => onNavigate(page.href)}
      aria-current={isCurrent ? "page" : undefined}
      className={cn(
        "mx-2 flex h-11 items-center gap-x-2.5 rounded-lg px-2 text-sm font-medium transition-colors",
        isCurrent
          ? "bg-surface text-foreground"
          : "text-foreground-body active:bg-surface",
      )}
    >
      {TOOL_ICONS[page.key] ?? (
        <page.icon className="size-5" strokeWidth={1.5} />
      )}
      {page.label}
      {page.tag && <NewTag tag={t(`tags.${page.tag}`)} />}
    </Link>
  );
};

/** Secondary pages as a two-column list of plain links. */
const DrawerLinks = ({
  pages,
  isCurrent: isPageCurrent,
  onNavigate,
  subtle,
}: {
  pages: SiteNavPage[];
  isCurrent: (page: SiteNavPage) => boolean;
  onNavigate: (href: string) => void;
  subtle?: boolean;
}) => (
  // text-body sits here: next to a text colour, cn() would merge it away.
  <div className="grid grid-cols-2 gap-x-2 px-2 text-body">
    {pages.map((page) => {
      const isCurrent = isPageCurrent(page);
      return (
        <Link
          key={page.key}
          to={page.href}
          onClick={() => onNavigate(page.href)}
          aria-current={isCurrent ? "page" : undefined}
          className={cn(
            "flex min-h-10 items-center rounded-lg px-2 py-1.5 leading-tight transition-colors",
            isCurrent
              ? "bg-surface text-foreground"
              : subtle
                ? "text-foreground-muted active:bg-surface"
                : "text-foreground-body active:bg-surface",
          )}
        >
          {page.label}
        </Link>
      );
    })}
  </div>
);
